import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import ProfessionalDashboardNavbar from './components/ProfessionalDashboardNavbar';
import ServiceRequestCard from './components/ServiceRequestCard';
import Footer from 'pages/landing-page/components/Footer';
import { Globe, Eye, Mail } from 'lucide-react';
import BillingStatusBanner from './components/BillingStatusBanner';

import serviceRequestService from 'services/serviceRequestService';
import Icon from 'components/AppIcon';

const ProfessionalDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [serviceRequests, setServiceRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(true);
  const [workArea, setWorkArea] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [filters, setFilters] = useState({
    serviceType: 'all',
    urgency: 'all',
    maxDistance: 'all',
    minCompensation: 0,
    maxCompensation: 10000
  });
  const [reviewCount, setReviewCount] = useState(null);

  // VIP / billing-exempt state
  const [billingExempt, setBillingExempt] = useState(false);
  const [vipBadge, setVipBadge] = useState({ label: 'Volaptic VIP', icon: '🌟' });

  // KPI state
  const [kpiLoading, setKpiLoading] = useState(true);
  const [preventiviAttesa, setPreventiviAttesa] = useState(null);
  const [lavoriConfermati, setLavoriConfermati] = useState(null);
  const [guadagnoMese, setGuadagnoMese] = useState(null);

  // Credits state
  const [creditsBalance, setCreditsBalance] = useState(null);
  const [creditsExpiresAt, setCreditsExpiresAt] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(true);

  // Public profile state
  const [publicProfile, setPublicProfile] = useState(null); // null = not loaded, false = no profile
  const [profileLoading, setProfileLoading] = useState(true);

  // Check authentication and payment authorization
  useEffect(() => {
    const checkAccess = async () => {
      if (authLoading) return;

      if (!user) {
        navigate('/login-form');
        return;
      }

      try {
        // Check registration step and payment verification
        const { data: userData, error: userError } = await supabase?.
        from('users')?.
        select('registration_step, stripe_payment_method_id, payment_authorized, role, billing_exempt')?.
        eq('id', user?.id)?.
        single();

        if (userError) {
          setError('Errore durante la verifica dell\'accesso');
          return;
        }

        // Store billing_exempt for VIP rendering
        setBillingExempt(userData?.billing_exempt === true);

        // Defense-in-depth: redirect admin/superadmin to /admin before any other check
        if (userData?.role === 'admin' || userData?.role === 'superadmin') {
          navigate('/admin', { replace: true });
          return;
        }

        if (userData?.registration_step < 2) {
          navigate('/professional-registration-step-2');
          return;
        }

        // Allow access when payment_authorized is true OR registration_step >= 3
        // Do NOT gate on stripe_payment_method_id alone
        const paymentCleared = userData?.payment_authorized === true || userData?.registration_step >= 3;
        if (!paymentCleared) {
          navigate('/payment-verification');
          return;
        }

        // Get professional's primary work area
        const { data: workAreaData, error: workAreaError } = await supabase?.
        from('professional_work_areas')?.
        select('*')?.
        eq('user_id', user?.id)?.
        eq('is_primary', true)?.
        single();

        if (!workAreaError && workAreaData) {
          setWorkArea(workAreaData);
        }
      } catch (err) {
        console.error('Access check error:', err);
        setError('Errore durante la verifica dell\'accesso');
      }
    };

    checkAccess();
  }, [user, authLoading, navigate]);

  // Fetch VIP badge from RPC
  useEffect(() => {
    const fetchVipBadge = async () => {
      try {
        const { data, error } = await supabase?.rpc('get_vip_badge');
        if (!error && data && data?.length > 0) {
          setVipBadge({ label: data?.[0]?.label ?? 'Volaptic VIP', icon: data?.[0]?.icon ?? '🌟' });
        }
      } catch (e) {
        // keep defaults
      }
    };
    fetchVipBadge();
  }, []);

  // Fetch public profile
  useEffect(() => {
    const fetchPublicProfile = async () => {
      if (!user) return;
      setProfileLoading(true);
      try {
        const { data: { user: authUser } } = await supabase?.auth?.getUser();
        if (!authUser) { setPublicProfile(false); return; }
        const { data, error: profileError } = await supabase?.from('professional_public_profiles')?.select('slug, is_published, manual_edit_count, view_count, lead_count')?.eq('user_id', authUser?.id)?.maybeSingle();

        if (profileError) {
          setPublicProfile(false);
        } else {
          setPublicProfile(data || false);
        }
      } catch (err) {
        setPublicProfile(false);
      } finally {
        setProfileLoading(false);
      }
    };
    fetchPublicProfile();
  }, [user]);

  // Fetch review count
  useEffect(() => {
    const fetchReviewCount = async () => {
      if (!user) return;
      try {
        const { count } = await supabase?.from('reviews')?.select('id', { count: 'exact', head: true })?.eq('professional_id', user?.id);
        setReviewCount(count ?? 0);
      } catch {
        setReviewCount(0);
      }
    };
    fetchReviewCount();
  }, [user]);

  // Fetch service requests
  useEffect(() => {
    const fetchRequests = async () => {
      if (!user) return;

      setLoading(true);

      // Fetch matched requests for this professional
      const { data: matchData, error: fetchError } = await supabase?.
      from('request_matches')?.
      select('id, professional_id, request_id, status, created_at')?.
      eq('professional_id', user?.id)?.
      neq('status', 'expired')?.
      order('created_at', { ascending: false });

      if (fetchError) {
        setError(fetchError?.message || 'Errore nel caricamento delle richieste');
      } else {
        // Step 2: fetch the actual service_requests using the request IDs
        const requestIds = (matchData || [])?.map((m) => m?.request_id)?.filter(Boolean);

        let serviceRequestsMap = {};
        if (requestIds?.length > 0) {
          const { data: srData } = await supabase?.
          from('service_requests')?.
          select('*')?.
          in('id', requestIds)?.
          in('status', ['pending', 'active']);

          console.log('service_requests raw result:', JSON.stringify(srData));

          (srData || [])?.forEach((sr) => {
            serviceRequestsMap[sr?.id] = sr;
          });
        }

        // Flatten: attach match status to the service_request object
        const requests = (matchData || [])?.
        map((match) => {
          const sr = serviceRequestsMap?.[match?.request_id];
          if (!sr) return null;
          return {
            ...sr,
            _match_status: match?.status,
            _match_id: match?.id
          };
        })?.
        filter(Boolean);

        console.log('opportunities mapped:', JSON.stringify(requests));

        const sortedRequests = [...(requests || [])]?.sort((a, b) => {
          const aEx = a?.assigned_professional_id === user?.id ? 1 : 0;
          const bEx = b?.assigned_professional_id === user?.id ? 1 : 0;
          if (aEx !== bEx) return bEx - aEx;
          return new Date(b?.created_at)?.getTime() - new Date(a?.created_at)?.getTime();
        });

        setServiceRequests(sortedRequests);
        setFilteredRequests(sortedRequests);

        // Count pending matches for KPI
        const pending = (matchData || [])?.filter((m) => m?.status === 'pending')?.length;
        setPendingCount(pending);
      }

      setLoading(false);
    };

    fetchRequests();
  }, [user]);

  // Real-time subscription
  useEffect(() => {
    if (!user) return;

    const channel = serviceRequestService?.subscribeToRequests((payload) => {
      if (payload?.eventType === 'INSERT') {
        setServiceRequests((prev) => [payload?.new, ...prev]);
        setFilteredRequests((prev) => [payload?.new, ...prev]);
        if ('Notification' in window && Notification?.permission === 'granted') {
          new Notification('Nuova richiesta di servizio', {
            body: payload?.new?.title || 'Una nuova richiesta è disponibile',
            icon: '/favicon.ico'
          });
        }
      } else if (payload?.eventType === 'UPDATE') {
        setServiceRequests((prev) =>
        prev?.map((req) => req?.id === payload?.new?.id ? payload?.new : req)
        );
        setFilteredRequests((prev) =>
        prev?.map((req) => req?.id === payload?.new?.id ? payload?.new : req)
        );
      } else if (payload?.eventType === 'DELETE') {
        setServiceRequests((prev) => prev?.filter((req) => req?.id !== payload?.old?.id));
        setFilteredRequests((prev) => prev?.filter((req) => req?.id !== payload?.old?.id));
      }
    });

    if ('Notification' in window && Notification?.permission === 'default') {
      Notification?.requestPermission();
    }

    return () => {
      serviceRequestService?.unsubscribeFromRequests(channel);
    };
  }, [user]);

  // Apply filters
  useEffect(() => {
    let filtered = [...serviceRequests];

    if (filters?.serviceType !== 'all') {
      filtered = filtered?.filter((req) => req?.service_type === filters?.serviceType);
    }

    if (filters?.urgency !== 'all') {
      filtered = filtered?.filter((req) => req?.urgency === filters?.urgency);
    }

    if (filters?.minCompensation > 0) {
      filtered = filtered?.filter(
        (req) => req?.estimated_compensation >= filters?.minCompensation
      );
    }

    if (filters?.maxCompensation < 10000) {
      filtered = filtered?.filter(
        (req) => req?.estimated_compensation <= filters?.maxCompensation
      );
    }

    setFilteredRequests(filtered);
  }, [filters, serviceRequests]);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch KPI counters
  useEffect(() => {
    const fetchKPIs = async () => {
      setKpiLoading(true);
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) {
          setPreventiviAttesa('—');
          setLavoriConfermati('—');
          setGuadagnoMese('—');
          return;
        }
        const uid = user?.id;

        // Compute current calendar month boundaries (UTC)
        const now = new Date();
        const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))?.toISOString();
        const monthEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))?.toISOString();

        const [sentRes, acceptedRes] = await Promise.all([
          supabase?.from('quotes')?.select('id', { count: 'exact', head: true })?.eq('professional_id', uid)?.eq('status', 'sent'),
          supabase?.from('quotes')?.select('id', { count: 'exact', head: true })?.eq('professional_id', uid)?.eq('status', 'accepted'),
        ]);

        setPreventiviAttesa(sentRes?.error ? '—' : sentRes?.count ?? 0);
        setLavoriConfermati(acceptedRes?.error ? '—' : acceptedRes?.count ?? 0);

        const { data: confirmedQuotes, error: earningsError } =
          await supabase?.from('reviews')
            ?.select('quote_id, created_at, quotes!inner(preview_price, professional_id)')
            ?.eq('quotes.professional_id', uid)
            ?.gte('created_at', monthStart)
            ?.lt('created_at', monthEnd);

        if (earningsError || !confirmedQuotes) {
          setGuadagnoMese('—');
        } else {
          const total = confirmedQuotes?.reduce(
            (sum, row) => sum + (parseFloat(row?.quotes?.preview_price) || 0), 0
          );
          setGuadagnoMese(
            '€ ' + total?.toLocaleString('it-IT', { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })
          );
        }
      } catch (e) {
        console.error('KPI fetch error:', e);
        setPreventiviAttesa('—');
        setLavoriConfermati('—');
        setGuadagnoMese('—');
      } finally {
        setKpiLoading(false);
      }
    };
    fetchKPIs();
  }, []);

  // Fetch credits balance and expiration
  useEffect(() => {
    const fetchCredits = async () => {
      setCreditsLoading(true);
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) {
          setCreditsBalance(null);
          setCreditsExpiresAt(null);
          return;
        }
        const uid = user?.id;

        const { data: creditsData, error: creditsError } = await supabase?.from('users')?.select('credits_balance, credits_expires_at')?.eq('id', uid)?.single();

        if (creditsError) {
          setCreditsBalance(null);
          setCreditsExpiresAt(null);
        } else {
          setCreditsBalance(creditsData?.credits_balance ?? null);
          setCreditsExpiresAt(creditsData?.credits_expires_at ?? null);
        }
      } catch (e) {
        console.error('Credits fetch error:', e);
        setCreditsBalance(null);
        setCreditsExpiresAt(null);
      } finally {
        setCreditsLoading(false);
      }
    };
    fetchCredits();
  }, []);

  // Format number as Italian Euro (e.g. "€ 1.498,00")
  const formatEuro = (value) => {
    return '€ ' + Number(value)?.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Skeleton pulse element
  const KpiSkeleton = () =>
  <span className="inline-block w-12 h-7 bg-gray-200 rounded animate-pulse align-middle" />;


  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Caricamento dashboard...</p>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <ProfessionalDashboardNavbar billingExempt={billingExempt} vipBadge={vipBadge} />
      {/* Billing Status Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BillingStatusBanner billingExempt={billingExempt} />
      </div>
      {/* Premium Hero Section */}
      <style>{`
        .pro-hero {
          min-height: 420px;
background-image: url(/assets/images/professional_dashboard_hero_bright_interior.png);
background-size: cover;
background-position: center right;
background-repeat: no-repeat;
        }
        .pro-hero-content {
          min-height: 420px;
        }
        @media (max-width: 767px) {
          .pro-hero {
background-position: center;
          }
          .pro-hero-mobile-overlay {
            display: block;
          }
        }
        @media (min-width: 768px) {
          .pro-hero-mobile-overlay {
            display: none;
          }
        }
      `}</style>
      <div
        className="pro-hero relative w-full overflow-hidden">

        {/* Gradient overlay: left side only for readability */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(90deg, rgba(16,77,152,0.35) 0%, rgba(16,77,152,0.20) 40%, rgba(0,0,0,0) 70%)'
          }} />

        {/* Mobile overlay for text readability */}
        <div
          className="pro-hero-mobile-overlay absolute inset-0"
          style={{ background: 'rgba(16,77,152,0.70)' }} />


        {/* Hero Content */}
        <div className="pro-hero-content relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center" style={{ paddingTop: '56px', paddingBottom: '56px' }}>
          <div className="max-w-2xl">

            {/* Main headline */}
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight mb-4" style={{ letterSpacing: '-0.02em' }}>
              Trova nuovi clienti<br />
              <span style={{ background: 'linear-gradient(90deg, #818cf8, #c7d2fe)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>ogni giorno</span>
            </h1>

            {/* Subtitle */}
            <p className="text-gray-300 text-lg mb-8 leading-relaxed" style={{ maxWidth: '480px' }}>
              Accedi alle richieste più rilevanti nella tua area, gestisci le trattative e fai crescere il tuo business.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => document.getElementById('requests-feed')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-xl font-semibold text-white text-base shadow-lg transition-all duration-200 hover:scale-105 active:scale-95"
                style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', boxShadow: '0 4px 24px rgba(99,102,241,0.35)' }}>

                <Icon name="Zap" size={18} />
                Vedi opportunità
              </button>
              <button
                onClick={() => navigate('/professional/trattative')}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-xl font-semibold text-white text-base border border-white/25 backdrop-blur-sm transition-all duration-200 hover:bg-white/10 hover:border-white/40 active:scale-95"
                style={{ background: 'rgba(255,255,255,0.07)' }}>

                <Icon name="Briefcase" size={18} />
                Le mie trattative
              </button>
            </div>
          </div>

          {/* Available requests text */}
          <div className="mt-10 pt-8 border-t border-white/10">
            <p className="text-white text-base font-medium">
              Richieste disponibili nella tua zona: <span className="font-bold">{filteredRequests?.length ?? 0}</span>
            </p>
          </div>
        </div>
        {/* Bottom fade-out overlay */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '80px',
            background: 'linear-gradient(to bottom, transparent, #ffffff)',
            pointerEvents: 'none',
            zIndex: 10
          }}
        />
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl font-bold text-gray-900">Dashboard Professionista</h2>
                <button
                  onClick={() => navigate('/professional/recensioni')}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer border border-amber-200"
                  style={{ color: '#92400e' }}>
                  <span style={{ color: '#fbbf24', fontSize: '14px' }}>★</span>
                  <span>{reviewCount !== null ? reviewCount : '—'}</span>
                  <span className="text-xs text-amber-700">recensioni</span>
                </button>
              </div>
              {workArea &&
              <p className="text-gray-600 mt-1 flex items-center gap-2">
                  <Icon name="MapPin" size={16} className="text-primary" />
                  Area di lavoro: {workArea?.name}
                </p>
              }
            </div>
          </div>

          {error &&
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800">{error}</p>
            </div>
          }
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {/* Preventivi in attesa */}
          <div
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow duration-200"
            onClick={() => navigate('/professional/trattative')}>

            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center">
              <Icon name="Clock" size={20} className="text-amber-500" />
            </div>
            <div>
              <p className="text-3xl font-extrabold text-gray-900 leading-none">
                {kpiLoading ? <KpiSkeleton /> : preventiviAttesa}
              </p>
              <p className="text-xs text-gray-500 mt-1">Preventivi in attesa</p>
            </div>
          </div>

          {/* Lavori confermati */}
          <div
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow duration-200"
            onClick={() => navigate('/professional/trattative')}>

            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Icon name="CheckCircle" size={20} className="text-emerald-500" />
            </div>
            <div>
              <p className="text-3xl font-extrabold text-gray-900 leading-none">
                {kpiLoading ? <KpiSkeleton /> : lavoriConfermati}
              </p>
              <p className="text-xs text-gray-500 mt-1">Lavori confermati</p>
            </div>
          </div>

          {/* Guadagno mese */}
          <div
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow duration-200"
            onClick={() => navigate('/professional/lavori-confermati')}>
            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center">
              <Icon name="TrendingUp" size={20} className="text-violet-500" />
            </div>
            <div>
              <p className="text-3xl font-extrabold text-gray-900 leading-none">
                {kpiLoading ? <KpiSkeleton /> : guadagnoMese}
              </p>
              <p className="text-xs text-gray-500 mt-1">Valore confermato mese</p>
            </div>
          </div>
        </div>

        {/* Credits Widget */}
        {!creditsLoading && !billingExempt && (
          <div
            className="mb-6 flex items-center justify-between px-4 py-4"
            style={{
              backgroundColor: '#e8f4fd',
              border: '1px solid #32b3e5',
              borderRadius: '8px',
            }}>
            {/* Left side */}
            <div className="flex items-center gap-3">
              <Icon name="Coins" size={20} style={{ color: '#32b3e5' }} />
              <div>
                {creditsBalance === 0 && !billingExempt ? (
                  <p className="text-sm font-medium" style={{ color: '#ea580c' }}>
                    Crediti carnet esauriti — stai usando il piano postpaid €0,99/preventivo
                  </p>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-gray-800">
                      Crediti disponibili: <span style={{ color: '#32b3e5' }}>{creditsBalance ?? 0}</span>
                    </p>
                    {creditsBalance > 0 && creditsExpiresAt && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Scadono il {new Date(creditsExpiresAt)?.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            {/* Right side */}
            <button
              onClick={() => navigate('/professional/crediti')}
              className="text-sm font-semibold text-white rounded-lg px-4 py-2 flex-shrink-0 ml-4"
              style={{ backgroundColor: '#104d98' }}>
              Acquista crediti →
            </button>
          </div>
        )}

        {/* Public Profile Card */}
        {!profileLoading &&
        <div className="mb-6">
            {(!publicProfile || publicProfile?.is_published === false) ? (
          /* No profile yet OR not published */
          (<div className="rounded-xl border border-blue-100 shadow-sm p-5 flex items-center justify-between" style={{ backgroundColor: '#f0f7ff' }}>
            <div className="flex items-center gap-3">
              <Globe size={28} color="#32b3e5" />
              <div>
                <p className="font-semibold text-gray-900">La tua pagina pubblica</p>
                <p className="text-sm text-gray-500">Oltre 30 professionisti sono già trovabili su Google da oggi.</p>
              </div>
            </div>
            <button
          onClick={() => navigate('/professional/pagina-pubblica')}
          className="text-sm font-semibold text-white rounded-lg px-4 py-2 flex-shrink-0"
          style={{ backgroundColor: '#104d98' }}>
              Crea ora →
            </button>
          </div>)) :
          (
          /* Profile exists and published */
          (<div className="rounded-xl border border-blue-100 shadow-sm p-5" style={{ backgroundColor: '#f0f7ff' }}>
            {/* Row 1: Title + Badge */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Globe size={20} color="#32b3e5" />
                <p className="font-bold text-gray-900">La tua pagina pubblica</p>
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Attiva</span>
            </div>
            {/* Row 2: URL */}
            <p className="text-sm text-gray-700 font-mono mb-3">volaptic.com/p/{publicProfile?.slug}</p>
            {/* Row 3: Stats */}
            <div className="flex items-center gap-6 mb-3">
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <Eye size={15} className="text-gray-400" />
                <span className="font-semibold text-gray-800">{publicProfile?.view_count ?? 0}</span>
                <span>visite totali</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <Mail size={15} className="text-gray-400" />
                <span className="font-semibold text-gray-800">{publicProfile?.lead_count ?? 0}</span>
                <span>richieste ricevute</span>
              </div>
            </div>
            {/* Row 4: Tagline */}
            <p className="text-sm text-gray-400 italic mb-4">I clienti cercano su Google e trovano te.</p>
            {/* Row 5: Buttons */}
            <div className="flex items-center justify-end gap-2 flex-wrap">
              <button
            onClick={() => window.open(`/p/${publicProfile?.slug}`, '_blank')}
            className="text-sm font-semibold text-gray-700 border border-gray-300 rounded-lg px-4 py-2 hover:bg-white/60 transition-colors">
                Visualizza
              </button>
              {(publicProfile?.manual_edit_count ?? 0) < 2 ? (
                <button
              onClick={() => navigate('/professional/pagina-pubblica')}
              className="text-sm font-semibold text-white rounded-lg px-4 py-2"
              style={{ backgroundColor: '#104d98' }}>
                  Modifica
                </button>
              ) : (
                <p className="text-xs text-gray-400">
                  Hai già modificato 2 volte —{' '}
                  <a href="mailto:contact@volaptic.com" className="underline hover:text-gray-600">
                    contatta l'assistenza
                  </a>
                </p>
              )}
            </div>
          </div>))
          }
          </div>
        }

        {/* Service Requests Feed — full width */}
        <div id="requests-feed" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">
              Opportunità per te ({filteredRequests?.length})
            </h3>
            <button
              onClick={() => window.location?.reload()}
              className="text-sm text-primary hover:text-primary/80 flex items-center gap-2">

              <Icon name="RefreshCw" size={16} />
              Aggiorna
            </button>
          </div>

          {filteredRequests?.length === 0 ?
          <div className="text-center py-12">
              <Icon name="Inbox" size={48} className="text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg mb-2">
                Nessuna richiesta disponibile al momento
              </p>
              <p className="text-gray-500 text-sm">
                Le nuove richieste appariranno qui in tempo reale
              </p>
            </div> :

          <div className="space-y-4">
              {filteredRequests?.map((request) =>
            <ServiceRequestCard
              key={request?.id}
              request={request}
              currentUserId={user?.id}
              matchStatus={request?._match_status}
              onViewed={() => setPendingCount((prev) => Math.max(0, prev - 1))} />

            )}
            </div>
          }
        </div>
      </div>
      <Footer />
    </div>
  );

};

export default ProfessionalDashboard;